"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const HUBSPOT_PORTAL_ID = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
const HUBSPOT_ENABLED = process.env.NEXT_PUBLIC_HUBSPOT_ENABLED === "true";

declare global {
  interface Window {
    dataLayer?: unknown[];
    _hsq?: Array<[string, ...unknown[]]>;
  }
}

function pushConsentToDataLayer(consent: {
  statistics: boolean;
  marketing: boolean;
  preferences: boolean;
}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "consent_update",
    consent: {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.statistics ? "granted" : "denied",
      functionality_storage: consent.preferences ? "granted" : "denied",
      personalization_storage: consent.preferences ? "granted" : "denied",
      security_storage: "granted",
    },
  });
}

// Re-fires consent_update on change so GTM tags respond without a reload.
export function ConsentScripts() {
  const { consent, hasDecided } = useConsent();
  const pathname = usePathname();
  const embedded = useIsEmbedded();

  useEffect(() => {
    if (!hasDecided || embedded) return;
    pushConsentToDataLayer({
      statistics: consent.statistics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    });
  }, [consent, hasDecided, embedded]);

  // HubSpot uses this to track page views on client-side route changes.
  useEffect(() => {
    if (window._hsq && consent.marketing && !embedded) {
      window._hsq.push(["setPath", pathname]);
      window._hsq.push(["trackPageView"]);
    }
  }, [pathname, consent.marketing, embedded]);

  return (
    <>
      {/* Google Tag Manager — loaded only after the user grants Statistics
          or Marketing consent. Consent Mode then controls which individual
          tags fire; defaults are set inline in the root layout's <head>. */}
      {!embedded && GTM_ID && hasDecided && (consent.statistics || consent.marketing) && (
        <Script id="gtm-loader" strategy="afterInteractive">{`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');
        `}</Script>
      )}

      {/* HubSpot tracking — strict opt-in under Marketing. */}
      {!embedded && HUBSPOT_PORTAL_ID && HUBSPOT_ENABLED && consent.marketing && (
        <Script
          id="hs-script-loader"
          strategy="afterInteractive"
          src={`//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`}
        />
      )}
    </>
  );
}
